import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTransition, animated, config } from 'react-spring';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { Photos, Wrapper, ScrollIndicatorContainer } from './styles';
import ScrollIndicator from '@spa/shared/components/Search/Card/Controls/ScrollIndicator';
import NavigationArrow from '@spa/shared/components/Search/Card/Controls/NavigationArrows';
import ArrowLeft from '@spa/shared/svg/arrow-left';
import ArrowRight from '@spa/shared/svg/arrow-right';
import { useSwipe } from '@spa/shared/hooks/swipe';
import Label from '@spa/shared/components/Search/Card/Labels';
import StarFull from '@spa/shared/svg/star-full';
import { StudentResidencesLogo } from '@spa/shared/svg/studentsresidences-logo';

const Gallery = ({
  photos,
  disableNavigationArrows = false,
  disableFullNavigation = false,
  title,
  isFeatured,
  isUniresidences,
  isLazy,
  ...props
}) => {
  const [{ active, translate }, , goLeft, goRight, handlers, resetTranslate] = useSwipe({ slides: photos });
  const { t } = useTranslation();
  const router = useRouter();
  const isInResidencesSearch = router.pathname.includes('/residences');

  const diff = translate === 100 ? 1 : -1;

  const transitions = useTransition(photos[active], {
    from: { transform: `translateX(${-(translate ? 300 * diff : translate)}px)` },
    enter: { transform: 'translateX(0px)' },
    leave: { transform: `translateX(${translate ? 300 * diff : translate}px)` },
    onRest: resetTranslate,
    config: config.stiff,
    keys: item => item.path
  });

  if (!photos.length) {
    return null;
  }

  const loading = { loading: isLazy ? 'lazy' : 'eager' };

  const handleNavigationGestures = disableFullNavigation ? {} : handlers;

  return (
    <Wrapper {...props}>
      <Photos {...handleNavigationGestures}>
        {!disableNavigationArrows && (
          <Fragment>
            <NavigationArrow arialabel="next" onClick={goRight}>
              <ArrowRight />
            </NavigationArrow>
            <NavigationArrow arialabel="previous" onClick={goLeft}>
              <ArrowLeft />
            </NavigationArrow>
          </Fragment>
        )}
        {transitions((style, item) => {
          return (
            <animated.div loading="lazy" key={item.path} style={style}>
              <img src={item.path} alt={title} style={{ objectFit: 'cover' }} {...loading} />
            </animated.div>
          );
        })}
        {!disableFullNavigation && (
          <ScrollIndicatorContainer>
            <ScrollIndicator activeIndex={active} count={photos.length} />
          </ScrollIndicatorContainer>
        )}
        {isFeatured && (
          <Label isFeatured={true} isUniresidences={false}>
            <StarFull />
            {t('results.offer.featured')}
          </Label>
        )}
        {isUniresidences && !isInResidencesSearch && (
          <Label isFeatured={false} isUniresidences={true}>
            <StudentResidencesLogo />
          </Label>
        )}
      </Photos>
    </Wrapper>
  );
};

Gallery.propTypes = {
  photos: PropTypes.array.isRequired,
  disableNavigationArrows: PropTypes.bool,
  disableFullNavigation: PropTypes.bool,
  isLazy: PropTypes.bool,
  title: PropTypes.string,
  isFeatured: PropTypes.bool,
  isUniresidences: PropTypes.bool
};

export default React.memo(Gallery);
